<template>
  <span class="text-left">
    <a class="row-action" href="javascript:;" @click="openAddReceipt">
      <i class="fas fa-camera" :style="{ color: color }"></i>
    </a>
    <ed-modal title="Submit Receipt"
              :show="showAddReceipt"
              @close="handleClose"
              @validate="handleConfirm"
              :primary-button-disabled="primaryButtonDisabled"
              primary-button="Submit"
              type="medium"
              validation>
      <!-- PRODUCTDEV-5802 (reopen, Case 4) — `loading` is the global old-UI
           spinner+overlay (main.scss:134), shown while a Submit-time receipt
           upload, a folder-receipt fetch/re-upload, or an additional-document
           upload is in flight. Submit is disabled for the same window. -->
      <div slot="body" class='add-receipt-overlay' :class="{ loading: receiptUploadInProgress }">
        <!-- Scan-enabled body: mirrors the Card-transactions Submit Receipt modal,
             with an extra "Add from receipt folder" entry point. Per-row context;
             the transaction is already known, so no ranker / candidate selector. -->
        <template v-if="featureFlags.receiptScanPrefill">
          <template v-if="uploadingReceipt">
            <label><strong>Uploading</strong></label>
            <div class="receipt-upload-progress">
              <div class="receipt-upload-progress-track">
                <div class="receipt-upload-progress-fill" :style="{ width: uploadProgressPct + '%' }"></div>
              </div>
              <span class="receipt-upload-progress-label">{{ uploadProgressPct }}%</span>
            </div>
          </template>

          <template v-else>
            <label><strong>Receipt</strong></label>
            <div v-if="pendingReceiptFile || attachedFile" class="receipt-file-chip">
              <i class="fas" :class="chipIconClass"></i>
              <a :href="chipFileUrl || undefined" target="_blank" rel="noopener" class="receipt-file-chip-name">{{ chipFileName }}</a>
              <span v-if="chipFileSize" class="receipt-file-chip-size">{{ chipFileSize }}</span>
              <a v-if="canManageReceipts" href="javascript:;" class="receipt-file-chip-remove" @click="clearPendingFile" v-tooltip="'Remove'">
                <i class="fas fa-times-circle"></i>
              </a>
            </div>
            <!-- PRODUCTDEV-5783 — the two entry points stay visible once a receipt is attached,
                 but DISABLED (mirrors the reconcile modal). Removing the receipt with the ✕
                 re-enables them. -->
            <div class="receipt-scan-actions">
              <!-- PRODUCTDEV-5788 — folder pick hidden when scanning is disabled (no scan/match).
                   PRODUCTDEV-5790 — the permission tooltip sits on a wrapping <span> (a disabled
                   native <button> swallows hover, so v-tooltip on the button never fires). Shown
                   only for the no-permission case, not the receipt-already-attached disable.
                   Mirrors the reconcile modal.
                   PRODUCTDEV-5805 — pendingFolderScanId joins the "receipt staged" disable set. -->
              <span v-if="!receiptCaptureDisabled && receiptScanModeLoaded"
                    v-tooltip="!canManageReceipts ? permissionTooltip : ((pendingReceiptFile || attachedFile || pendingFolderScanId) ? 'Remove the current receipt to upload a different one' : '')">
                <button type="button" class="receipt-action-button"
                        :disabled="!canManageReceipts || !!(pendingReceiptFile || attachedFile || pendingFolderScanId)" @click="openFolderPicker">
                  ADD FROM RECEIPT FOLDER
                </button>
              </span>
              <span v-tooltip="!canManageReceipts ? permissionTooltip : ((pendingReceiptFile || attachedFile || pendingFolderScanId) ? 'Remove the current receipt to upload a different one' : '')">
                <button type="button" class="receipt-action-button"
                        :disabled="!canManageReceipts || !!(pendingReceiptFile || attachedFile || pendingFolderScanId)" @click="triggerFileInput">
                  UPLOAD NEW RECEIPT
                </button>
              </span>
            </div>
            <!-- PRODUCTDEV-5783 — envelopes always carry coding, so a newly-picked receipt never
                 auto-scans. Submit saves it with the coding unchanged; Rescan runs Photon on demand
                 and updates the coding from this receipt. Always shown while a receipt is staged.
                 PRODUCTDEV-5788 — hidden when scanning is disabled (no Photon path at all).
                 PRODUCTDEV-5790 — also hidden when the row is read-only (`disabled`): a card admin can
                 replace the receipt on a locked envelope but must NOT change coding, and Scan would
                 overwrite it. !disabled is this surface's "can edit coding" signal, mirroring the
                 modal's `reconcileAction !== 'view'` gate on the same prompt.
                 PRODUCTDEV-5805 — also shows for a staged folder receipt (pendingFolderScanId). -->
            <div v-if="(pendingReceiptFile || pendingFolderScanId) && !receiptCaptureDisabled && !disabled" class="replace-coding-alert">
              <span class="replace-coding-alert-text">Coding stays unchanged on Submit — Scan to update it from this receipt.</span>
              <a href="javascript:;" class="replace-coding-alert-action" @click="rescanReceipt">Scan Receipt</a>
            </div>
          </template>

          <!-- PRODUCTDEV-5783 — Additional Documents: supporting files, NOT receipts. Never
               Photon-scanned, never enter the receipt/coding flow. Plain Legacy byte-upload
               (no progress bar). Mirrors the reconcile modal's Additional Documents section. -->
          <div class="additional-documents">
            <label><strong>Additional Documents</strong></label>
            <div v-for="(doc, docIndex) in additionalDocuments" :key="`doc-${docIndex}`" class="receipt-file-chip">
              <i class="fas" :class="documentIconClass(doc)"></i>
              <a :href="(doc.path || doc._blobUrl) || undefined" target="_blank" rel="noopener" class="receipt-file-chip-name">{{ doc.name }}</a>
              <span v-if="doc.size" class="receipt-file-chip-size">{{ formatFileSize(doc.size) }}</span>
              <a v-if="canManageReceipts" href="javascript:;" class="receipt-file-chip-remove" @click="removeDocument(docIndex)" v-tooltip="'Remove'">
                <i class="fas fa-times-circle"></i>
              </a>
            </div>
            <div v-if="canManageReceipts" class="receipt-scan-actions">
              <button type="button" class="receipt-action-button" @click="uploadNewDocument">UPLOAD FILE</button>
            </div>
          </div>

          <input type="file"
                 ref="receiptUploadInput"
                 accept="application/pdf, image/png, image/jpeg"
                 @change="handleFileSelected"
                 style="display:none">

          <ReceiptFolderPicker
            :show="showFolderPicker"
            :is-processing="folderPickerIsProcessing"
            @select="handleFolderSelect"
            @close="closeFolderPicker" />
        </template>

        <!-- Flag-off fallback: keep the original ed-upload widget unchanged. -->
        <template v-else>
          <span style="font-size: 12px">Accepted formats: pdf, png, jpg, jpeg</span>
          <div class="receipt-upload-legacy">
            <ed-upload button-label='Upload New Receipt'
                       :class='{loading: !uploadFinished}'
                       v-model='uploaderFiles'
                       v-tooltip="!(isUserAdmin || isDraft) && disabled ? permissionTooltip : ''"
                       readonly
                       :multiple='false'
                       :postAction='api.env + api.$.evedpaycard.receipt'
                       :uploadAuto='true'
                       :canOpenFile='true'
                       @input='createAttachments'
                       :headers='{Authorization: "Bearer " + token}'
                       :disabled="!(isUserAdmin || isDraft) && disabled"
                       name='attachment_upload'
                       accept='application/pdf, image/png, image/jpeg'
                       extensions='pdf,png,jpg,jpeg'>
            </ed-upload>
          </div>
        </template>
      </div>
    </ed-modal>
  </span>
</template>

<script>
  import receiptScansApi from '@/core/api/receiptScans'
  import compressReceiptImage from '@/core/receiptScan/imageCompression'
  import createReceiptScanPoller from '@/core/receiptScan/receiptScanPoller'
  import featureFlags from '@/core/feature-flags/feature-flags'
  import ReceiptFolderPicker from './ReceiptFolderPicker.vue'

  export default {
    components: { ReceiptFolderPicker },
    data() {
      return {
        showAddReceipt: false,
        uploaderFiles: [],
        attachedPath: [],
        token: this.$user.sessionSettings.Token,
        color: '#000000',
        initialAttachedFiles: [],
        submitPressed: false,
        uploadFinished: true,
        platformUploadAttempted: false,
        featureFlags: featureFlags,
        // PRODUCTDEV-5788 — per-division "No Receipt Capture" gate for this envelope row.
        // Default false (a fetch failure must not hide affordances on an enabled division).
        // When true: hide the folder pick + the "Scan Receipt" rescan prompt so the user
        // can't trigger Photon. The plain Submit path (saveReceiptWithoutScan) already
        // attaches without scanning, so no upload-logic change is needed.
        receiptCaptureDisabled: false,
        // PRODUCTDEV-5788 — false until this row's scan mode resolves; gates the folder button
        // so it doesn't flash in before "No Receipt Capture" is known.
        receiptScanModeLoaded: false,
        // Scan-enabled state (mirrors Card-transactions/index.vue Submit Receipt modal)
        pendingReceiptFile: null,
        // PRODUCTDEV-5804 — object URL for the pending file so the chip link carries a real href
        // (right-click "Open in new tab"). Lifecycle owned by the pendingReceiptFile watcher.
        pendingReceiptObjectUrl: '',
        // PRODUCTDEV-5805 — a receipt picked from the folder is staged (not auto-applied) just
        // like an uploaded file: holds its scan id so the "Scan to update coding" prompt shows and
        // Scan Receipt applies its prefill in the division's mode. Mirrors reconcile's deferred folder rescan.
        pendingFolderScanId: null,
        uploadingReceipt: false,
        uploadProgressPct: 0,
        // PRODUCTDEV-5802 (reopen, Case 4) — old-UI spinner over the modal body +
        // Submit disabled while a plain byte-upload window is in flight (Submit's
        // no-scan receipt save, the folder fetch/re-upload, an additional-document
        // upload). The 4s bar (uploadingReceipt) stays reserved for the Scan flow.
        receiptUploadInProgress: false,
        // Already-attached file (re-opened envelope): show the chip, allow remove.
        attachedFile: null,
        showFolderPicker: false,
        folderPickerIsProcessing: false,
        // PRODUCTDEV-5783 — additional (non-receipt) documents for this row. Display shape
        // (name/size/path/...); emitted to the parent in the Legacy TransactionFile shape.
        additionalDocuments: [],
        // PRODUCTDEV-5783 — modal staging: changes (new receipt, ✕ removal, document add/remove)
        // commit to the parent ONLY on Submit. `dirty` enables Submit; `receiptRemoved` records a
        // staged receipt deletion so Submit can emit []. Cancel reverts everything (no emit).
        dirty: false,
        receiptRemoved: false
      }
    },

    props: {
      rowData: Object,
      incomplete: false,
      attachments: Object,
      // PRODUCTDEV-5783 — already-saved additional documents for this transaction (parent-owned).
      documents: Array,
      disabled: false,
      isDraft: false
    },

    async created() {
      try {
        this.uploaderFiles = this.mapUploaderFiles(this.attachments)
        this.attachedFile = this.mapAttachedFile(this.attachments)
        this.populateAdditionalDocuments(this.documents)
        this.changeIconColor()
        // PRODUCTDEV-5788 — resolve this row's division receipt-scan mode. Default false
        // on any failure so an enabled division never loses its scan affordances.
        var divisionId = this.rowData && this.rowData.DivisionId
        if (this.featureFlags.receiptScanPrefill && divisionId != null) {
          try {
            var scanSetting = await receiptScansApi.getScanMode(divisionId)
            this.receiptCaptureDisabled = !!(scanSetting && scanSetting.prefillMode === 'scanning_disabled')
          } catch (modeErr) {
            console.warn('Could not fetch receipt-scan mode (defaulting to enabled):', modeErr)
            this.receiptCaptureDisabled = false
          }
        }
      } catch (e) {
        console.error('Error fetching transaction files:', e)
      }
      // PRODUCTDEV-5788 — reveal the scan actions once the mode is resolved (or the load
      // settled), so the folder button never flashes before No-Capture is known.
      this.receiptScanModeLoaded = true
    },

    watch: {
      // PRODUCTDEV-5804 — own the pending file's object URL lifecycle (revoke old, create new) so the
      // chip link has a stable href. Covers every assignment site to pendingReceiptFile.
      pendingReceiptFile(file) {
        if (this.pendingReceiptObjectUrl) { URL.revokeObjectURL(this.pendingReceiptObjectUrl); this.pendingReceiptObjectUrl = '' }
        if (file) this.pendingReceiptObjectUrl = URL.createObjectURL(file)
      },
      incomplete() {
        this.changeIconColor()
      },
      attachments(newValues) {
        this.uploaderFiles = newValues ? newValues.map(file => this.mapFile(file)) : {}
        this.attachedFile = this.mapAttachedFile(newValues)
        this.changeIconColor()
      },
      documents(newValues) {
        this.populateAdditionalDocuments(newValues)
      }
    },
    computed: {
      isUserAdmin() {
        if (this.$user.$.userPermissions.includes('EvedPayCardAdministrator')) {
          return true
        }
        return false
      },
      permissionTooltip() {
        return 'You do not have permission to change receipt attachments on this transaction.'
      },
      // PRODUCTDEV-5790 — receipt + additional-document editing is allowed whenever the row is
      // editable, OR for a card admin (or a Draft envelope) even when the row is otherwise
      // read-only (`disabled`). Restores the PRODUCTDEV-4071 admin exception that the V2 UI
      // dropped. submitFiles() persists an admin's locked-row change through update-receipt.
      canManageReceipts() {
        return !this.disabled || this.isUserAdmin || this.isDraft
      },
      primaryButtonDisabled() {
        if (this.featureFlags.receiptScanPrefill) {
          if (this.uploadingReceipt) return true
          // PRODUCTDEV-5802 (reopen, Case 4) — a byte-upload is in flight; Submit
          // would race a file that isn't persisted/staged yet.
          if (this.receiptUploadInProgress) return true
          // PRODUCTDEV-5790 — read-only rows have nothing to submit, EXCEPT a card admin (or a
          // Draft envelope) who can still replace the receipt / add-remove documents. They commit
          // via submitFiles' update-receipt branch, gated on `dirty` below like everyone else.
          if (!this.canManageReceipts) return true
          // Submit commits ALL staged changes (new receipt, ✕ removal, document add/remove), so
          // it's enabled whenever something is staged. The folder path still commits on pick.
          return !this.dirty
        }
        return (!this.uploadFinished || this.disabled) && !this.isUserAdmin
      },
      chipFileName() {
        if (this.pendingReceiptFile) return this.pendingReceiptFile.name
        if (this.attachedFile) return this.attachedFile.OriginalName || this.attachedFile.MediaFile
        return ''
      },
      chipFileSize() {
        if (this.pendingReceiptFile) return this.formatFileSize(this.pendingReceiptFile.size)
        return ''
      },
      // PRODUCTDEV-5804 — URL for the chip link's href: pending file → object URL; already-attached → durable path.
      chipFileUrl() {
        if (this.pendingReceiptFile) return this.pendingReceiptObjectUrl
        if (this.attachedFile) return this.attachedFile.FullPath || this.attachedFile.path || ''
        return ''
      },
      chipIconClass() {
        var name = (this.chipFileName || '').toLowerCase()
        if (/\.pdf$/.test(name)) return 'fa-file-pdf'
        if (/\.(png|jpe?g|gif|webp)$/.test(name)) return 'fa-file-image'
        return 'fa-file'
      }
    },
    methods: {
      openAddReceipt() {
        this.showAddReceipt = true
        this.initialAttachedFiles = this.deepCopy(this.uploaderFiles)
        this.submitPressed = false
        this.uploadFinished = true
        this.dirty = false
        this.receiptRemoved = false
        // PRODUCTDEV-5805 — start each open clean so a folder receipt staged-but-not-scanned in a
        // prior session doesn't resurface the "Scan to update coding" prompt.
        this.pendingFolderScanId = null
      },

      // Kept for backward compatibility with the flag-off path that toggles via
      // showModal elsewhere; scan-on opens via openAddReceipt directly.
      showModal(showAddReceipt) {
        this.showAddReceipt = !showAddReceipt
        if (this.showAddReceipt) {
          this.initialAttachedFiles = this.deepCopy(this.uploaderFiles)
          this.submitPressed = false
          this.uploadFinished = true
        }
      },

      handleClose() {
        if (this.uploadingReceipt || this.receiptUploadInProgress) return
        this.showAddReceipt = false
        this.changeIconColor()
        this.goBackToInitialValue()
        this.resetScanState()
        // PRODUCTDEV-5783 — Cancel reverts staged changes: re-hydrate the receipt + documents
        // from the parent's canonical props (we never emitted, so they're still original).
        if (!this.submitPressed) {
          this.attachedFile = this.mapAttachedFile(this.attachments)
          this.populateAdditionalDocuments(this.documents)
          this.receiptRemoved = false
          this.dirty = false
        }
      },

      resetScanState() {
        this.pendingReceiptFile = null
        this.pendingFolderScanId = null
        this.uploadProgressPct = 0
        this.receiptUploadInProgress = false
        this.showFolderPicker = false
        this.folderPickerIsProcessing = false
        this.cancelUploadProgressBar()
        if (this.$refs.receiptUploadInput) {
          this.$refs.receiptUploadInput.value = ''
        }
      },

      goBackToInitialValue() {
        if (!this.submitPressed) {
          this.uploaderFiles = this.initialAttachedFiles
        }
        this.changeIconColor()
      },

      changeIconColor() {
        this.incomplete = this.incomplete && ((this.uploaderFiles && this.uploaderFiles.length === 0) || !this.uploaderFiles)
        this.color = this.incomplete ? '#FF0000' : (this.uploaderFiles && this.uploaderFiles.length ? '#4A90E2' : '#000000')
      },

      mapUploaderFiles(files) {
        files = files || []

        let result = []
        files.forEach((file) => {
          result.push(this.mapFile(file))
        })
        return result
      },

      mapAttachedFile(files) {
        if (!files || !files.length) return null
        return files[0]
      },

      mapFile(file) {
        var parts = file.OriginalName.split('.')
        var extension = ''
        var fileType = ''
        if (parts.length > 1) {
          extension = parts[parts.length - 1].toLowerCase()
        }
        switch (extension) {
          case 'png':
            fileType = 'image/png'
            break
          case 'pdf':
            fileType = 'application/pdf'
            break
          case 'jpg':
            fileType = 'image/jpg'
            break
          case 'jpeg':
            fileType = 'image/jpeg'
            break
          default:
            fileType = ''
        }
        return {
          name: file.OriginalName,
          type: fileType,
          size: file.SizeBytes,
          file: file.MediaFile,
          mediaFile: file.MediaFile,
          path: file.FullPath,
          pathWithName: file.PathWithName,
          id: file.Id,
          success: true,
          readonly: this.disabled && !this.isUserAdmin
        }
      },

      // ===== Scan-enabled flow =====

      triggerFileInput() {
        if (this.$refs.receiptUploadInput) {
          this.$refs.receiptUploadInput.click()
        }
      },

      handleFileSelected(event) {
        var files = event.target.files
        if (!files || files.length === 0) return
        var file = files[0]
        var parts = file.name ? file.name.split('.') : []
        var extension = parts.length > 1 ? parts[parts.length - 1].toLowerCase() : ''
        var ALLOWED = ['pdf', 'png', 'jpg', 'jpeg']
        if (ALLOWED.indexOf(extension) === -1) {
          this.showWarningMessage('Please upload a file with one of these extensions: .pdf, .png, .jpg, .jpeg')
          if (this.$refs.receiptUploadInput) {
            this.$refs.receiptUploadInput.value = ''
          }
          return
        }
        var MAX_FILE_SIZE = 10 * 1024 * 1024
        if (file.size >= MAX_FILE_SIZE) {
          this.showWarningMessage('File is too large. Please upload a receipt smaller than 10 MB.')
          if (this.$refs.receiptUploadInput) {
            this.$refs.receiptUploadInput.value = ''
          }
          return
        }
        this.pendingReceiptFile = file
        this.dirty = true
      },

      clearPendingFile() {
        this.pendingReceiptFile = null
        // PRODUCTDEV-5805 — also drop a staged folder receipt so the Scan-to-update prompt hides.
        this.pendingFolderScanId = null
        if (this.$refs.receiptUploadInput) {
          this.$refs.receiptUploadInput.value = ''
        }
        // PRODUCTDEV-5783 — STAGE the removal: don't emit now (that persisted even on Cancel).
        // `receiptRemoved` makes Submit emit []; Cancel re-hydrates from the parent's props.
        if (this.attachedFile) {
          this.attachedFile = null
          this.receiptRemoved = true
        }
        this.dirty = true
      },

      // ===== PRODUCTDEV-5783 — Additional Documents (mirrors the reconcile modal) =====
      // Supporting files, NOT receipts: never scanned, never enter the receipt/coding flow.
      // Plain Legacy byte-upload (no progress bar); emitted to the parent in TransactionFile shape.
      uploadNewDocument() {
        var input = document.createElement('input')
        input.type = 'file'
        input.accept = 'application/pdf, image/png, image/jpeg'
        var self = this
        input.onchange = async function (e) {
          var file = e.target.files && e.target.files[0]
          if (!file) return
          var parts = file.name ? file.name.split('.') : []
          var ext = parts.length > 1 ? parts[parts.length - 1].toLowerCase() : ''
          if (['pdf', 'png', 'jpg', 'jpeg'].indexOf(ext) === -1) {
            self.showWarningMessage('Please upload a file with one of these extensions: .pdf, .png, .jpg, .jpeg')
            return
          }
          if (file.size >= 10 * 1024 * 1024) {
            self.showWarningMessage('File is too large. Please upload a document smaller than 10 MB.')
            return
          }
          var fileType = ext === 'png' ? 'image/png'
            : ext === 'pdf' ? 'application/pdf'
            : ext === 'jpg' ? 'image/jpg'
            : ext === 'jpeg' ? 'image/jpeg'
            : (file.type || '')
          var localObjectUrl = URL.createObjectURL(file)
          // Optimistic chip; NOT part of the save payload until pathWithName lands.
          var doc = {
            name: file.name,
            type: fileType,
            size: file.size,
            file: '',
            mediaFile: '',
            path: localObjectUrl,
            pathWithName: '',
            id: '',
            success: true,
            readonly: false,
            _blobUrl: localObjectUrl
          }
          self.additionalDocuments.push(doc)
          // PRODUCTDEV-5802 (reopen, Case 4) — old-UI spinner + Submit disabled
          // while the byte-upload is in flight, so Submit can't commit a document
          // that has no PathWithName yet (it would be silently dropped from the
          // payload by emitAdditionalDocumentsUpdated's filter).
          self.receiptUploadInProgress = true
          try {
            var legacy = await self.uploadReceiptToLegacy(file)
            if (legacy && legacy.pathWithName) {
              doc.pathWithName = legacy.pathWithName
              doc.mediaFile = legacy.mediaFile
              doc.file = legacy.mediaFile
              doc.path = legacy.path
              // PRODUCTDEV-5783 — staged: commits on Submit, reverts on Cancel. (The backend now
              // persists documents end-to-end, so Submit is all that's needed.)
              self.dirty = true
            } else {
              var idx = self.additionalDocuments.indexOf(doc)
              if (idx !== -1) self.additionalDocuments.splice(idx, 1)
              if (doc._blobUrl) URL.revokeObjectURL(doc._blobUrl)
              self.showWarningMessage('File "' + file.name + '" could not be uploaded. Please try again.')
            }
          } finally {
            self.receiptUploadInProgress = false
          }
        }
        input.click()
      },

      removeDocument(index) {
        var doc = this.additionalDocuments[index]
        if (!doc) return
        if (doc._blobUrl) URL.revokeObjectURL(doc._blobUrl)
        this.additionalDocuments.splice(index, 1)
        // PRODUCTDEV-5783 — staged; commits on Submit, reverts on Cancel.
        this.dirty = true
      },

      documentIconClass(doc) {
        var name = ((doc && doc.name) || '').toLowerCase()
        if (/\.pdf$/.test(name)) return 'fa-file-pdf'
        if (/\.(png|jpe?g|gif|webp)$/.test(name)) return 'fa-file-image'
        return 'fa-file'
      },

      // Build the Legacy TransactionFile-shaped array (only finalized docs with a
      // pathWithName) and emit to the parent. Keeps each doc's Id so a save that
      // doesn't touch docs re-sends the existing rows and the BE keeps them.
      emitAdditionalDocumentsUpdated() {
        var vendorId = this.$root.getUser.$.data.VendorID
        var vci = this.$root.getUser.$.data.VCI
        var emitted = this.additionalDocuments
          .filter(item => item && item.pathWithName)
          .map(item => ({
            OriginalName: item.name,
            SizeBytes: item.size,
            MediaFile: item.mediaFile,
            AttachmentFile: item.file,
            PathWithName: item.pathWithName,
            FullPath: item.path,
            ReadOnly: item.readonly || false,
            Id: item.id,
            VendorId: vendorId,
            VCI: vci
          }))
        this.$emit('additional-documents-updated', emitted)
      },

      // Hydrate the document chips from already-saved docs (parent passes them in).
      populateAdditionalDocuments(documents) {
        this.additionalDocuments = []
        if (!Array.isArray(documents)) return
        var self = this
        documents.forEach(function (file) {
          var parts = (file.OriginalName || '').split('.')
          var ext = parts.length > 1 ? parts[parts.length - 1].toLowerCase() : ''
          var fileType = ext === 'png' ? 'image/png'
            : ext === 'pdf' ? 'application/pdf'
            : ext === 'jpg' ? 'image/jpg'
            : ext === 'jpeg' ? 'image/jpeg'
            : ''
          self.additionalDocuments.push({
            name: file.OriginalName,
            type: fileType,
            size: file.SizeBytes,
            file: file.MediaFile,
            mediaFile: file.MediaFile,
            path: file.FullPath,
            pathWithName: file.PathWithName,
            id: file.Id,
            success: true,
            readonly: false
          })
        })
      },

      openFolderPicker() {
        this.showFolderPicker = true
      },

      closeFolderPicker() {
        this.showFolderPicker = false
        this.folderPickerIsProcessing = false
      },

      // Folder path: bytes live in Platform S3 only. To keep parity with the
      // upload path's canonical PathWithName (the documented reopen-500 trap if
      // missing), fetch the bytes and re-upload to Legacy, then claim to the
      // row's known transaction. No loading bar — folder pick is already the
      // confirmation; ReceiptFolderPicker shows its own isProcessing state.
      async handleFolderSelect(event) {
        var receiptScanId = event && event.receiptScanId
        if (!receiptScanId || this.folderPickerIsProcessing || this.receiptUploadInProgress) return
        this.folderPickerIsProcessing = true
        // PRODUCTDEV-5802 (reopen, Case 4) — close the picker right away and show
        // the old-UI spinner over the Submit Receipt modal for the whole
        // fetch/re-upload window, with Submit disabled (receiptUploadInProgress
        // feeds primaryButtonDisabled). Previously the only feedback was the
        // picker's disabled ADD button.
        this.closeFolderPicker()
        this.receiptUploadInProgress = true
        try {
          var statusData = await receiptScansApi.getStatus(receiptScanId)
          if (!statusData || statusData.status !== 'FolderSaved') {
            this.showWarningMessage('Receipt is no longer in the folder — pick another.')
            this.receiptUploadInProgress = false
            return
          }

          var imgRes = null
          try { imgRes = await receiptScansApi.getImage(receiptScanId) } catch (imgErr) { console.warn('Could not fetch folder receipt image:', imgErr) }
          var fileName = (imgRes && imgRes.fileName) || 'Receipt'
          var contentType = (imgRes && imgRes.contentType) || ''
          // Pull the bytes through the Platform proxy (/image-bytes) rather than a raw
          // browser fetch of the S3 presigned URL — the proxy is same-origin via the BFF
          // so it isn't subject to the S3 CORS wall that would otherwise leave blob null
          // and hard-refuse the attach.
          var blob = null
          try {
            blob = await receiptScansApi.getImageBytes(receiptScanId)
            if (blob) contentType = blob.type || contentType
          } catch (bytesErr) { console.warn('Could not fetch folder receipt bytes:', bytesErr) }

          var legacyMeta = null
          if (blob) {
            var legacyFile = new File([blob], fileName, { type: blob.type || contentType || 'application/octet-stream' })
            legacyMeta = await this.uploadReceiptToLegacy(legacyFile)
          }
          if (!legacyMeta) {
            // Without a Legacy PathWithName we'd reproduce the documented reopen-500.
            // Refuse the attach instead of attaching with an empty path.
            this.showWarningMessage('Could not attach receipt — please try again.')
            this.receiptUploadInProgress = false
            return
          }

          // PRODUCTDEV-5783 — do NOT claim here. The folder receipt stays FolderSaved (in the
          // folder) until the envelope is SAVED; CreateEnvelope.claimPendingFolderReceipts() runs
          // the deferred FolderSaved -> Matched claim on save success, so cancelling the envelope
          // never strands the scan (mirrors the reconcile flow's deferred claim).
          var attachment = this.buildAttachment({
            name: fileName,
            type: contentType,
            size: blob ? blob.size : 0,
            mediaFile: legacyMeta.mediaFile,
            pathWithName: legacyMeta.pathWithName,
            path: legacyMeta.path,
            receiptScanId: receiptScanId
          })

          this.submitPressed = true
          this.$emit('attachmentsUpdated', [attachment])
          this.$emit('receiptScanId', receiptScanId)
          // PRODUCTDEV-5805 — parity with the upload + reconcile flow: do NOT auto-apply the scan
          // here. Auto-apply both bypassed the division's scan mode (the folder receipt always came
          // back subtotal_and_tax) and skipped the "Scan to update coding" prompt. Stage the folder
          // scan and keep the modal open so that prompt shows; Scan Receipt then applies the prefill
          // in the division's configured mode (see rescanReceipt + fetchPrefill).
          // PRODUCTDEV-5805 FIX — a prior ✕ (clearPendingFile) stages receiptRemoved=true so Submit
          // emits []. Picking a folder receipt SUPERSEDES that removal, so clear the flag. Without
          // this, the folder path (which never sets pendingReceiptFile, so it falls through to the
          // receiptRemoved branch in handleConfirm instead of returning early like the upload path)
          // wipes the just-added receipt on Submit.
          this.receiptRemoved = false
          this.pendingFolderScanId = receiptScanId
          this.dirty = true
          this.receiptUploadInProgress = false
          this.$notifications.notify({ type: 'success', message: 'Receipt added from folder', placement: { from: 'top', align: 'center' } })
          this.changeIconColor()
        } catch (error) {
          // PRODUCTDEV-5802 (reopen) — the picker is already closed (closed on ADD),
          // so every error path just lifts the spinner and warns; re-opening the
          // folder is one click away.
          this.receiptUploadInProgress = false
          var status = error && error.response && error.response.status
          if (status === 409) {
            this.showWarningMessage('Receipt state changed — pick another.')
          } else if (status === 403) {
            this.showWarningMessage('You no longer have access to this receipt.')
          } else {
            console.error('Folder receipt claim failed:', error)
            this.showWarningMessage('Failed to add receipt. Please try again.')
          }
        }
      },

      // Upload path: Legacy-first byte upload + Platform submit with the row's tx
      // as upload context (auto-link → no ranker) + prefill, all behind the 4s
      // pacing bar from the Card-transactions Submit Receipt modal.
      async handleConfirm() {
        if (this.featureFlags.receiptScanPrefill) {
          if (this.uploadingReceipt || this.receiptUploadInProgress) return
          this.submitPressed = true
          // PRODUCTDEV-5805 — Submit resolves the staged folder receipt (the user chose NOT to
          // scan it), so drop the flag that drives the "Scan to update coding" prompt.
          this.pendingFolderScanId = null
          // PRODUCTDEV-5783 — commit staged additional-document changes on every Submit.
          this.emitAdditionalDocumentsUpdated()
          if (this.pendingReceiptFile) {
            // PRODUCTDEV-5783 — envelopes always have coding, so Submit SAVES the receipt without
            // scanning (coding untouched). Photon runs only via Rescan. Legacy upload + attachment.
            await this.saveReceiptWithoutScan()
            return
          }
          // No new receipt staged.
          // PRODUCTDEV-5790 — a receipt is required on submitted / non-Draft envelopes: block
          // Submit when no receipt is left (e.g. the user removed the last one), mirroring the
          // host's update-receipt guard and the old legacy path. Keep the modal open so the user
          // can re-add or Cancel (Cancel re-hydrates the receipt). Draft / no-envelope: no requirement.
          if (this.rowData.EnvelopeId && !this.isDraft && !this.pendingReceiptFile && !this.attachedFile) {
            this.$notifications.notify({
              type: 'info-warn',
              message: 'Receipt is required. Please upload at least one file.',
              placement: { from: 'top', align: 'center' }
            })
            return
          }
          // Commit a staged receipt removal (if any), then close.
          if (this.receiptRemoved) {
            this.$emit('attachmentsUpdated', [])
          }
          // PRODUCTDEV-5826 — no new receipt staged, but an admin may have changed additional
          // documents on a locked envelope. The parent can't save a read-only envelope, so persist
          // here via update-receipt, re-sending the existing receipt so it isn't soft-deleted.
          if (this.disabled && this.isUserAdmin) {
            await this.persistLockedAdminUpdate(this.attachedFile ? [this.attachedFile] : [])
          }
          this.showAddReceipt = false
          this.changeIconColor()
          return
        }
        // Flag-off legacy path — preserve the original timing: the prior
        // `@confirm` listener auto-closed the modal on click and submitFiles
        // ran "after". Now under `validation` mode the modal won't auto-close,
        // so close FIRST, then run submitFiles so the user sees the same
        // instant-close UX as before.
        this.changeIconColor()
        if (this.rowData.EnvelopeId && (!this.newFiles || this.newFiles.length === 0) && !this.isDraft) {
          this.$notifications.notify({
            type: 'info-warn',
            message: 'Receipt is required. Please upload at least one file.',
            placement: { from: 'top', align: 'center' }
          })
          this.showAddReceipt = false
          return
        }
        this.showAddReceipt = false
        await this.submitFiles()
      },

      async submitUploadFlow() {
        var file = this.pendingReceiptFile
        var compressed = file
        try {
          compressed = await compressReceiptImage(file)
        } catch (e) {
          console.warn('Image compression failed, falling back to original file:', e)
        }

        this.uploadingReceipt = true
        this.uploadProgressPct = 0
        var self = this

        // 4s pacing bar — same shape as Card-transactions/index.vue + reconcile.
        // The bar covers the perceived wait; the heavy Photon /api/v4 work
        // (~15s) continues in the background and applies prefill to the row
        // after the modal closes.
        var barDuration = 4000
        var barStart = performance.now()
        var barComplete = new Promise(function (resolve) {
          var tick = function (now) {
            var pct = Math.min(100, Math.round(((now - barStart) / barDuration) * 100))
            self.uploadProgressPct = pct
            if (pct < 100) {
              self._uploadProgressRaf = requestAnimationFrame(tick)
            } else {
              self._uploadProgressRaf = null
              resolve()
            }
          }
          self._uploadProgressRaf = requestAnimationFrame(tick)
        })

        var legacyPromise = this.uploadReceiptToLegacy(compressed)

        var formData = new FormData()
        formData.append('file', compressed)
        var idempotencyKey = this.generateIdempotencyKey()
        // Auto-link the Platform receipt to the row's known transaction —
        // skips the ranker/CandidateSelector entirely (per Option D).
        var legacyTxId = this.rowData && this.rowData.Id
        var platformPromise = receiptScansApi.submitReceipt(formData, idempotencyKey, {
          uploadContextLegacyTransactionId: legacyTxId,
          uploadSurface: 'envelope'
        }).catch(function (err) {
          console.warn('Platform receipt submit failed (legacy still attached):', err)
          return null
        })

        // PRODUCTDEV-5783 — track whether Photon resolves during the bar so we can skip the
        // "scanning" toast + the coding-row skeleton when the data is already in hand at bar end.
        var scanResolvedEarly = false
        platformPromise.then(function () { scanResolvedEarly = true })

        try {
          // Wait bar + Legacy (Legacy typically resolves under the bar's 4s).
          await barComplete
          var legacyMeta = await legacyPromise

          if (!legacyMeta) {
            this.showWarningMessage('Receipt upload failed. Please try again.')
            this.uploadingReceipt = false
            this.uploadProgressPct = 0
            return
          }

          // Build + emit the attachment now so the row's chip lands at 4s. The
          // ReceiptScanId is patched in when the Platform promise resolves
          // (below), and a follow-up emit pushes the updated attachment.
          var attachment = this.buildAttachment({
            name: this.pendingReceiptFile.name,
            type: this.pendingReceiptFile.type,
            size: this.pendingReceiptFile.size,
            mediaFile: legacyMeta.mediaFile,
            pathWithName: legacyMeta.pathWithName,
            path: legacyMeta.path,
            receiptScanId: null
          })

          this.submitPressed = true
          this.$emit('attachmentsUpdated', [attachment])

          // Close modal at bar end — matches the Submit Receipt UX. Prefill
          // applies to the row in background when Photon finishes.
          this.uploadingReceipt = false
          this.uploadProgressPct = 0
          this.pendingReceiptFile = null
          this.showAddReceipt = false
          this.changeIconColor()

          // PRODUCTDEV-5783 — if Photon already finished during the bar, the prefill is in hand:
          // no "scanning" toast, no skeleton. Otherwise tell the parent to show the coding-row
          // skeleton + a scanning toast until the prefill lands (cleared by scan-ended / receiptScan).
          if (!scanResolvedEarly) {
            this.$emit('scan-started')
            this.notifyReceipt('info', 'Scanning receipt')
          }

          // Fire prefill application in background. Caught here so an
          // unhandled rejection can't propagate after the modal closed.
          this.applyPrefillInBackground(platformPromise, attachment).catch(function (err) {
            console.warn('Background prefill failed:', err)
          })
        } catch (err) {
          console.error('Receipt upload flow failed:', err)
          this.uploadingReceipt = false
          this.uploadProgressPct = 0
          this.cancelUploadProgressBar()
          this.showWarningMessage('Receipt upload failed. Please try again.')
        }
      },

      // PRODUCTDEV-5783 — Submit path for envelopes: save the staged receipt to Legacy and
      // attach it WITHOUT scanning (coding always exists, so we never auto-run Photon). The user
      // opts into Photon via Rescan. No prefill, no receiptScan emit — coding is left untouched.
      async saveReceiptWithoutScan() {
        var file = this.pendingReceiptFile
        if (!file) return
        // PRODUCTDEV-5802 (reopen, Case 4) — no scan, no progress bar, but the modal
        // now stays OPEN with the old-UI spinner and Submit disabled until the
        // Legacy upload actually lands. Closing immediately (the previous behavior)
        // made the upload invisible — it looked like nothing was happening, and a
        // failure surfaced after the modal was already gone.
        var self = this
        this.receiptUploadInProgress = true
        var compressed = file
        try {
          compressed = await compressReceiptImage(file)
        } catch (e) {
          console.warn('Image compression failed, falling back to original file:', e)
        }
        try {
          var legacyMeta = await this.uploadReceiptToLegacy(compressed)
          if (!legacyMeta) {
            self.receiptUploadInProgress = false
            // handleConfirm pre-set submitPressed; the modal stays open for a retry,
            // so reset it or a later Cancel skips its staged-state revert.
            self.submitPressed = false
            self.showWarningMessage('Receipt upload failed. Please try again.')
            return
          }
          var attachment = self.buildAttachment({
            name: file.name,
            type: file.type,
            size: file.size,
            mediaFile: legacyMeta.mediaFile,
            pathWithName: legacyMeta.pathWithName,
            path: legacyMeta.path,
            receiptScanId: null
          })
          self.submitPressed = true
          self.pendingReceiptFile = null
          self.$emit('attachmentsUpdated', [attachment])
          // PRODUCTDEV-5826 — on a locked envelope the parent can't save (read-only), so an admin's
          // new receipt plus any document changes are persisted here via update-receipt. Runs
          // under the PRODUCTDEV-5802 spinner: the modal stays open and Submit disabled until
          // the persist also lands, then closes.
          if (self.disabled && self.isUserAdmin) {
            await self.persistLockedAdminUpdate([attachment])
          }
          self.receiptUploadInProgress = false
          self.showAddReceipt = false
          self.changeIconColor()
        } catch (err) {
          console.error('Receipt save failed:', err)
          // Keep the modal open with the staged file intact so the user can retry.
          // submitPressed was pre-set by handleConfirm; reset it so a later Cancel
          // still reverts staged state.
          self.receiptUploadInProgress = false
          self.submitPressed = false
          self.showWarningMessage('Receipt upload failed. Please try again.')
        }
      },

      // PRODUCTDEV-5783 — opt-in scan: the user chose to update the coding from the staged
      // receipt. Runs the same Legacy-upload + Photon /api/v4 + prefill flow the receipt used to
      // run automatically on Submit (submitUploadFlow emits attachmentsUpdated + receiptScan).
      async rescanReceipt() {
        // PRODUCTDEV-5805 — folder receipt: it's already scanned, so apply its EXISTING prefill
        // (no Photon re-run) in the row division's configured mode, mirroring reconcile's folder
        // rescan and the upload "Scan Receipt" that updates the coding.
        if (this.pendingFolderScanId) {
          var folderScanId = this.pendingFolderScanId
          this.pendingFolderScanId = null
          var legacyTxId = this.rowData && this.rowData.Id
          var divisionId = this.rowData && this.rowData.DivisionId
          this.$emit('scan-started')
          var prefillData = await this.fetchPrefill(folderScanId, divisionId)
          if (prefillData) {
            this.$emit('receiptScan', { receiptScanId: folderScanId, prefill: prefillData, legacyTransactionId: legacyTxId })
          } else {
            this.notifyReceipt('info-warn', "Receipt attached, but its details couldn't be read automatically. Please enter the amount split and description manually.")
          }
          this.$emit('scan-ended')
          this.showAddReceipt = false
          this.changeIconColor()
          return
        }
        if (!this.pendingReceiptFile) return
        this.submitUploadFlow()
      },

      // Background continuation after the modal closes: wait for Platform's
      // submitReceipt (sync Photon /api/v4 ~15s), then emit receiptScanId +
      // prefill so CreateEnvelope populates the row's split form + description.
      async applyPrefillInBackground(platformPromise, attachment) {
        var legacyTxId = this.rowData && this.rowData.Id
        var platformData = await platformPromise
        if (!platformData || !platformData.receiptScanId) {
          // Legacy upload succeeded (the chip is on the row), but Platform/Photon
          // didn't return a scan to code from. Say so instead of failing silently.
          this.notifyReceipt('info-warn', "Receipt attached, but its details couldn't be read automatically. Please enter the amount split and description manually.")
          this.$emit('scan-ended')
          return
        }

        var receiptScanId = platformData.receiptScanId

        // Patch ReceiptScanId into the chip's attachment so save serializes it.
        attachment.ReceiptScanId = receiptScanId
        this.$emit('attachmentsUpdated', [attachment])
        this.$emit('receiptScanId', receiptScanId)

        // Mirror reconcile's pattern: try the inline /prefill first (Photon is
        // often already done by the time we reach here since submitReceipt is
        // sync). If 409/404/sentinel, fall back to the poller.
        var prefillData = await this.fetchPrefillWithFallback(receiptScanId)
        if (prefillData) {
          this.$emit('receiptScan', { receiptScanId: receiptScanId, prefill: prefillData, legacyTransactionId: legacyTxId })
        } else {
          // Scan finished but no prefill came back — don't leave the row looking
          // like nothing happened.
          this.notifyReceipt('info-warn', "Receipt attached, but its details couldn't be read automatically. Please enter the amount split and description manually.")
        }
        // PRODUCTDEV-5783 — scan finished: clear the coding-row skeleton (the prefill above
        // already populated the rows if it came back).
        this.$emit('scan-ended')
      },

      async fetchPrefillWithFallback(receiptScanId) {
        // Inline first — cheap and usually returns immediately after submitReceipt.
        try {
          var prefillData = await receiptScansApi.getPrefill(receiptScanId)
          if (prefillData) return prefillData
        } catch (err) {
          var status = err && err.response && err.response.status
          var notReady = status === 409 || status === 404 || (err && err.isPlatformSentinelError)
          if (!notReady) {
            console.warn('Prefill fetch failed:', err)
            return null
          }
          // Not ready yet — fall through to poller.
        }
        return await this.waitForPrefill(receiptScanId)
      },

      // Poll Photon until terminal state, then fetch prefill once.
      waitForPrefill(receiptScanId) {
        var self = this
        return new Promise(function (resolve) {
          var poller = createReceiptScanPoller({
            receiptScanId: receiptScanId,
            contextHint: { source: 'AddReceipt' },
            onDone: function () {
              self.fetchPrefill(receiptScanId).then(resolve).catch(function () { resolve(null) })
            },
            onError: function (err) {
              console.warn('Receipt scan poller error:', err)
              resolve(null)
            }
          })
          poller.start()
        })
      },

      async fetchPrefill(receiptScanId, targetDivisionId) {
        try {
          // PRODUCTDEV-5805 — pass the row's division so a folder receipt prefills in that
          // division's configured mode instead of the BE default (subtotal_and_tax).
          var prefillData = await receiptScansApi.getPrefill(receiptScanId, targetDivisionId)
          return prefillData || null
        } catch (err) {
          var status = err && err.response && err.response.status
          if (status !== 409 && status !== 404 && !(err && err.isPlatformSentinelError)) {
            console.warn('Prefill fetch failed:', err)
          }
          return null
        }
      },

      async uploadReceiptToLegacy(file) {
        var legacyUrl = this.api && this.api.env ? this.api.env + this.api.$.evedpaycard.receipt : null
        if (!legacyUrl) return null
        var legacyFormData = new FormData()
        legacyFormData.append('attachment_upload', file)
        try {
          var resp = await this.api.post(legacyUrl, legacyFormData, {
            headers: { 'Authorization': 'Bearer ' + this.token, 'Content-Type': 'multipart/form-data' }
          })
          var body = resp && resp.data
          if (typeof body === 'string') {
            try { body = JSON.parse(body) } catch (e) { body = null }
          }
          if (!body || !body.success || !body.path) return null
          var match = body.path.match(/(ClientData\/.*)/)
          var pathWithName = match && match[1] ? match[1] : body.path
          var mediaFile = body.file || pathWithName.split('/').pop()
          return { path: body.path, pathWithName: pathWithName, mediaFile: mediaFile }
        } catch (err) {
          console.warn('Legacy receipt upload failed:', err)
          return null
        }
      },

      buildAttachment(input) {
        var vendorId = this.$root.getUser.$.data.VendorID
        var vci = this.$root.getUser.$.data.VCI
        return {
          OriginalName: input.name || 'Receipt',
          SizeBytes: input.size || 0,
          MediaFile: input.mediaFile || '',
          AttachmentFile: input.mediaFile || '',
          Id: '',
          PathWithName: input.pathWithName || '',
          ReadOnly: false,
          VendorId: vendorId,
          FullPath: input.path || '',
          VCI: vci,
          ReceiptScanId: input.receiptScanId || null
        }
      },

      generateIdempotencyKey() {
        if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
          return crypto.randomUUID()
        }
        // RFC4122 v4 fallback for older browsers (Platform requires Guid.TryParse)
        var template = 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'
        return template.replace(/[xy]/g, function (c) {
          var r = (Math.random() * 16) | 0
          var v = c === 'x' ? r : (r & 0x3) | 0x8
          return v.toString(16)
        })
      },

      cancelUploadProgressBar() {
        if (this._uploadProgressRaf) {
          cancelAnimationFrame(this._uploadProgressRaf)
          this._uploadProgressRaf = null
        }
      },

      formatFileSize(bytes) {
        if (!bytes) return ''
        if (bytes < 1024) return bytes + ' B'
        if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB'
        return (bytes / (1024 * 1024)).toFixed(1) + ' MB'
      },

      // ===== Flag-off legacy flow (unchanged) =====

      async createAttachments(e) {
        e.forEach(x => {
          var parts = x.name.split('.')
          var extension = ''
          if (parts.length > 1) {
            extension = parts[parts.length - 1]
          }
          if (!['pdf', 'png', 'jpg', 'jpeg'].includes(extension.toLowerCase())) {
            this.uploaderFiles = this.uploaderFiles.filter(x => x.success)
            this.showWarningMessage(`The file "${x.name}" is not compatible. Please upload files with one of the following extensions: .pdf, .png, .jpg, .jpeg`)
            return
          }

          const MAX_FILE_SIZE = 10 * 1024 * 1024 // 10 MB
          if (x.size >= MAX_FILE_SIZE) {
            this.uploaderFiles = this.uploaderFiles.filter(x => x.size <= MAX_FILE_SIZE)
            this.showWarningMessage(`The file "${x.name}" is too large. Please upload files smaller than 10 MB.`)
            return
          }
        })
        let completedFiles = e.filter(
          item => item.success || // files uploaded with success
            (!item.success && item.response && typeof item.response === 'string' && !this.isJson(item.response)) // files where requested failed
        )

        if (completedFiles.length === e.length) {
          this.uploadFinished = true

          let uploadedFiles = e.map(async (item) => {
            let uploadError = ''
            let itemResponse = null
            let clientDataPath = ''
            let mediaFile = ''
            let receiptScanId = null

            if (item.success && item.response) {
              itemResponse = JSON.parse(item.response)
              if (itemResponse.success) {
                const regex = /(ClientData\/.*)/
                const match = itemResponse.path.match(regex)
                if (match && match.length > 1) {
                  const filePath = match[1]
                  clientDataPath = filePath.substring(filePath.indexOf('ClientData'))
                  this.attachedPath.push(clientDataPath)
                  const splits = clientDataPath.split('/')
                  mediaFile = splits[splits.length - 1]

                  // Platform upload: single file only
                  if (item.file && item.file instanceof Blob) {
                    try {
                      receiptScanId = await this.tryPlatformUpload(item.file)
                      if (receiptScanId) {
                        this.$emit('receiptScanId', receiptScanId)
                      }
                    } catch (platformErr) {
                      // 4xx error already handled in tryPlatformUpload
                      // Just continue with legacy file processing
                    }
                  }
                } else {
                  console.log('No matching files.')
                }
              }
              uploadError = (itemResponse && itemResponse.uploadError) ? itemResponse.uploadError : ''
            }
            if (!item.success) {
              uploadError = 'Upload request failed'
            }

            const vendorId = this.$root.getUser.$.data.VendorID
            const VCI = this.$root.getUser.$.data.VCI

            if (uploadError) {
              this.showWarningMessage('File "' + item.name + '": ' + uploadError)
            } else {
              return {
                OriginalName: item.name,
                SizeBytes: item.size,
                MediaFile: item.mediaFile ? item.mediaFile : mediaFile || '',
                AttachmentFile: itemResponse ? itemResponse.file : (item.file || ''),
                Id: item.id ? item.id : '',
                PathWithName: item.pathWithName ? item.pathWithName : itemResponse ? clientDataPath : '',
                ReadOnly: item.readonly || false,
                VendorId: vendorId,
                FullPath: item.path ? item.path : itemResponse.path ? itemResponse.path : '',
                VCI: VCI,
                ReceiptScanId: receiptScanId
              }
            }
          })

          this.mediaFilesPath = this.attachedPath.join(', ')
          this.$emit('mediaFilesPathUpdated', this.mediaFilesPath)
          this.attachedPath = []

          try {
            this.uploaderFiles = this.uploaderFiles.filter(item =>
              item.success &&
              (
                (!item.response && item.name) || // take existing items from edit
                (JSON.parse(item.response).success) // take new added items
              )
            ).map(item => {
              if (item.response) {
                const response = JSON.parse(item.response)
                if (response.path) {
                  item.path = response.path // assign the 'path' property from the response to the item
                }
              }
              return item
            })
          } catch (ee) {
            console.error(ee)
          }

          // Resolve all uploadedFiles promises
          Promise.all(uploadedFiles).then(resolvedFiles => {
            this.newFiles = resolvedFiles.filter(item => item)
            this.$forceUpdate()
          }).catch(err => {
            console.error('Error processing uploaded files:', err)
          })
        } else {
          this.uploadFinished = false
        }
      },

      deepCopy(obj) {
        if (obj === null || typeof obj !== 'object') {
          return obj
        }

        if (Array.isArray(obj)) {
          return obj.map(element => this.deepCopy(element))
        }

        const newObj = {}
        for (const key in obj) {
          if (obj.hasOwnProperty(key)) {
            newObj[key] = this.deepCopy(obj[key])
          }
        }

        return newObj
      },

      async submitFiles() {
        this.submitPressed = true
        this.$emit('attachmentsUpdated', this.newFiles)

        if (this.disabled && this.isUserAdmin) {
          // PRODUCTDEV-5790 — also carry additional documents so an admin can add/remove them
          // (not just the receipt) on a locked envelope row. The BE keeps docs by Id and
          // soft-deletes any finalized doc not in the list; this.additionalDocuments was
          // hydrated from the saved set on open, so an untouched doc set round-trips unchanged.
          const vendorId = this.$root.getUser.$.data.VendorID
          const payload = {
            transactionId: this.rowData.Id,
            receiptFiles: this.newFiles.map(f => ({
              id: f.Id || '00000000-0000-0000-0000-000000000000',
              pathWithName: f.PathWithName,
              originalName: f.OriginalName,
              mediaFile: f.MediaFile,
              sizeBytes: f.SizeBytes,
              vendorId: f.VendorId
            })),
            additionalDocuments: this.additionalDocuments
              .filter(d => d && d.pathWithName)
              .map(d => ({
                id: d.id || '00000000-0000-0000-0000-000000000000',
                pathWithName: d.pathWithName,
                originalName: d.name,
                mediaFile: d.mediaFile,
                sizeBytes: d.size,
                vendorId: d.vendorId || vendorId
              }))
          }

          try {
            await this.api.post(
              this.api.$.evedpaycard.updateReceipt,
              payload
            )
            this.$notifications.notify({
              type: 'info',
              message: 'Receipt updated successfully',
              placement: { from: 'top', align: 'center' }
            })
          } catch (e) {
            console.error('Error while updating the receipt:', e)
          }
        }
      },
      // PRODUCTDEV-5826 — persist a receipt/document change made from the ENVELOPE PAGE on a
      // locked (submitted / approved / waiting-approval) envelope. The scan-on Submit path only
      // emits to the parent, but CreateEnvelope can't run its full envelope save while the envelope
      // is read-only, so an admin's change was silently lost (the update-receipt request was never
      // made). Mirror the reconcile modal: POST the narrow update-receipt endpoint directly (coding
      // untouched). Gated on `disabled && isUserAdmin` so Draft / editable envelopes still persist
      // through the normal envelope save (no double-write). Sends the FULL desired receipt +
      // document sets — the BE keeps files by Id and soft-deletes any not in the list, so an
      // untouched set round-trips unchanged. Mirrors submitFiles' flag-off payload.
      async persistLockedAdminUpdate(receiptFiles) {
        const vendorId = this.$root.getUser.$.data.VendorID
        const payload = {
          transactionId: this.rowData.Id,
          receiptFiles: (receiptFiles || []).map(f => ({
            id: f.Id || '00000000-0000-0000-0000-000000000000',
            pathWithName: f.PathWithName,
            originalName: f.OriginalName,
            mediaFile: f.MediaFile,
            sizeBytes: f.SizeBytes,
            vendorId: f.VendorId
          })),
          additionalDocuments: this.additionalDocuments
            .filter(d => d && d.pathWithName)
            .map(d => ({
              id: d.id || '00000000-0000-0000-0000-000000000000',
              pathWithName: d.pathWithName,
              originalName: d.name,
              mediaFile: d.mediaFile,
              sizeBytes: d.size,
              vendorId: d.vendorId || vendorId
            }))
        }
        try {
          await this.api.post(this.api.$.evedpaycard.updateReceipt, payload)
          this.$notifications.notify({
            type: 'info',
            message: 'Receipt updated successfully',
            placement: { from: 'top', align: 'center' }
          })
        } catch (e) {
          console.error('Error while updating the receipt:', e)
        }
      },
      isJson(str) {
        try {
          JSON.parse(str)
        } catch (e) {
          return false
        }
        return true
      },

      showWarningMessage(message) {
        this.$nextTick(() => {
          this.$notifications.notify({
            type: 'plain',
            icon: 'exclamation-circle',
            placement: { from: 'top', align: 'center' },
            timeout: 0,
            message: message,
            discard: 'Ok',
            goto: ''
          })
        })
      },

      // Auto-dismissing toast for receipt-scan progress/outcome (distinct from
      // showWarningMessage, which is a sticky, dismiss-required validation warning).
      notifyReceipt(type, message) {
        this.$notifications.notify({
          type: type,
          message: message,
          placement: { from: 'top', align: 'center' }
        })
      },

      async tryPlatformUpload(file) {
        if (!this.featureFlags.receiptScanPrefill) {
          return null
        }

        try {
          // Canvas-compress images (PDFs pass through). Legacy already has the
          // uncompressed file by the time we reach this method — the <ed-upload>
          // widget posts to Legacy first — so this only shrinks the bytes that
          // hit Platform/Photon. Still worthwhile since Photon's /api/v4 is
          // synchronous and large images dominate the wall-clock.
          var compressed = await compressReceiptImage(file)
          var formData = new FormData()
          formData.append('file', compressed)

          var idempotencyKey = this.generateIdempotencyKey()
          var data = await receiptScansApi.submitReceipt(formData, idempotencyKey)

          if (data && data.receiptScanId) {
            this.platformUploadAttempted = true
            return data.receiptScanId
          }

          return null
        } catch (error) {
          if (error.isPlatformSentinelError) {
            console.warn('Platform receipt upload failed (legacy fallback triggered):', error)
            return null
          }

          console.error('Platform receipt upload failed (not a fallback scenario):', error)
          this.showWarningMessage('File upload to Platform failed. Please try again.')
          throw error
        }
      }
    },

    beforeDestroy() {
      this.cancelUploadProgressBar()
      if (this.pendingReceiptObjectUrl) URL.revokeObjectURL(this.pendingReceiptObjectUrl)
    }
  }
</script>

<style lang='scss' scoped>
  .add-receipt-overlay {
    background-color: white;
    padding: 20px;
    border: 1px solid #ccc;
    border-radius: 4px;
    box-shadow: 0 2px 6px rgba(0, 0, 0, 0.2);
    min-width: 480px;
  }

  // Buttons: Figma "Button / Secondary / Active" (node 549:2721). Same style
  // the design system uses for reconcile + Submit Receipt.
  .receipt-scan-actions {
    display: flex;
    gap: 12px;
    margin-top: 8px;
  }

  // PRODUCTDEV-5804 — separate the Additional Documents section from the receipt buttons above.
  .additional-documents {
    margin-top: 16px;
  }

  // PRODUCTDEV-5783 — "coding stays / Rescan" prompt shown while a new receipt is staged
  // (mirrors the reconcile modal's replace-coding alert).
  .replace-coding-alert {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 10px;
    background-color: #FFFBE6;
    border: 1px solid #FFE58F;
    border-radius: 2px;
    padding: 8px 16px;
    margin-top: 8px;
  }

  .replace-coding-alert-text {
    font-size: 14px;
    line-height: 22px;
    color: rgba(0, 0, 0, 0.85);
  }

  .replace-coding-alert-action {
    flex: 0 0 auto;
    font-size: 14px;
    line-height: 22px;
    color: #1890ff;
    cursor: pointer;
    white-space: nowrap;

    &:hover {
      color: #40a9ff;
    }
  }

  .receipt-action-button {
    border: 1px solid #999;
    background-color: #f8f8f8;
    padding: 7px 17px;
    text-transform: uppercase;
    font-family: 'Source Sans Pro', sans-serif;
    font-size: 14px;
    font-weight: 600;
    letter-spacing: 0.18px;
    cursor: pointer;
    border-radius: 3px;
    color: #333;
    transition: background-color 0.15s ease;

    &:hover:not(:disabled) {
      background-color: #efefef;
    }

    &:disabled {
      opacity: 0.6;
      cursor: not-allowed;
    }
  }

  /* PRODUCTDEV-5790 — disabled receipt buttons still show the permission tooltip via a wrapping
     <span> (a disabled native <button> swallows hover, so the span must receive pointer events).
     Scoped with `> span >` so the Additional Documents button (a direct child) is unaffected.
     Mirrors the reconcile modal. */
  .receipt-scan-actions > span {
    display: inline-flex;
    cursor: not-allowed;
  }

  .receipt-scan-actions > span > .receipt-action-button:disabled {
    pointer-events: none;
  }

  // Progress bar — mirrors Card-transactions/index.vue Submit Receipt modal.
  .receipt-upload-progress {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 12px 0 24px;

    .receipt-upload-progress-track {
      flex: 1;
      height: 8px;
      background: #F5F5F5;
      border-radius: 100px;
      overflow: hidden;
    }

    .receipt-upload-progress-fill {
      height: 100%;
      background: #1890FF;
      border-radius: 100px;
      transition: width 60ms linear;
    }

    .receipt-upload-progress-label {
      font-family: Roboto, sans-serif;
      font-size: 14px;
      line-height: 22px;
      color: rgba(0, 0, 0, 0.85);
      width: 32px;
      text-align: right;
    }
  }

  // File chip — selected/attached receipt row.
  .receipt-file-chip {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 12px 8px;
    // PRODUCTDEV-5804 — dividers only BETWEEN adjacent chips (the + rule below). A lone Receipt
    // shows no stray line; Additional Documents are separated with no leading/trailing line.
    margin: 4px 0 0;
    font-size: 12px;

    > i.fas {
      flex-shrink: 0;
      font-size: 18px;
    }
    // PRODUCTDEV-5804 — restore colored, file-type-specific icons (ed-upload palette) the V2 chip dropped.
    > i.fa-file-pdf   { color: #cb0404; }
    > i.fa-file-image { color: #4598d9; }
    > i.fa-file       { color: #acacac; }
  }

  .receipt-file-chip + .receipt-file-chip {
    border-top: 1px solid rgba(0, 0, 0, 0.08);
  }

  .receipt-file-chip-name {
    flex: 1 1 auto;
    min-width: 0;
    color: #4a90e2;
    text-decoration: underline;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .receipt-file-chip-size {
    color: #999;
    font-size: 12px;
  }

  .receipt-file-chip-remove {
    color: #888;
    text-decoration: none;
    font-size: 18px;
    line-height: 0;
    margin-left: 4px;

    &:hover {
      color: #333;
    }
  }
</style>



// WEBPACK FOOTER //
// src/components/_modules/Programs/Card-transactions/AddReceipt.vue